import _ from "lodash";
import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import { useI18n } from "Hooks";
import classNames from "classnames";

import GroupedList from "../GroupedList";
import {
  getGradeList,
  canUserCreateEventForEveryone,
} from "MyCalendar/components/EventModalV2/modules/Utils";
import { getOrganizationGradesQuery } from "MyCalendar/components/EventModalV2/modules/EventModalV2Query";

import { SearchInput, Checkbox } from "@toddle-edu/ds-web";
import {
  getOrganizationId,
  getOrganizationCurriculumPrograms,
  getSelectedCurriculumProgram,
} from "modules/CommonSelectors";
import withLoader from "UIComponents/GqlWithLoader";
import ACLStorage from "lib/aclService";
import { CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION } from "Constants/permissionConstants";

import classes from "./GradeSelectionList.scss";
import { getSelectedSchoolDetailsForCalendar } from "Teacher/routes/Courses/routes/Course/routes/MyCalendar/modules/Utils";

const GradeSelectionList = ({
  grades,
  curriculumPrograms,
  selectedGradeList,
  setSelectedGradeList,
  selectedUserTypes,
  viewOnly,
  testId,
}) => {
  const componentTestId = testId ? `${testId}-gradeList` : "gradeList";
  const { t } = useI18n();
  const [searchText, setSearchText] = useState("");

  const allGradeLength = _.size(grades);
  const isAllSelected = _.size(selectedGradeList) === allGradeLength;

  const gradeItemList = useMemo(
    () =>
      _.filter(grades, ({ name }) =>
        _.includes(_.lowerCase(name), _.lowerCase(searchText))
      ),
    [searchText, grades]
  );

  const gradeListWithCurriculum = useMemo(
    () => getGradeList({ gradeList: gradeItemList, curriculumPrograms }),
    [gradeItemList, curriculumPrograms]
  );

  const selectAllGrades = () => {
    setSelectedGradeList(
      isAllSelected
        ? []
        : _.flatMap(gradeListWithCurriculum, ({ options }) => options)
    );
  };

  const scrollContainerClasses = classNames({
    [classes.scrollContainer]: true,
    [classes.viewOnlyScrollContainer]: viewOnly,
  });

  return (
    <>
      {viewOnly ? null : (
        <div className={classes.searchContainer}>
          <SearchInput
            focusSearchBySlash={true}
            placeholder={t("announcement:search_grades")}
            onChange={setSearchText}
            value={searchText}
            testId={componentTestId}
          />
        </div>
      )}
      <div className={scrollContainerClasses}>
        {viewOnly || _.size(searchText) ? null : (
          <div
            className={classes.selectAllContainer}
            onClick={selectAllGrades}
            data-test-id={`${componentTestId}-selectAll-button`}
          >
            <div className={classes.avatarLabelContainer}>
              <div className="text-label">{t("common:all_grades")}</div>
            </div>
            <Checkbox
              isChecked={isAllSelected}
              testId={`${componentTestId}-all`}
            />
          </div>
        )}
        <GroupedList
          viewOnly={viewOnly}
          itemList={gradeListWithCurriculum}
          selectedItemsList={selectedGradeList}
          setSelectedItemsList={setSelectedGradeList}
          selectedUserTypes={selectedUserTypes}
          testId={componentTestId}
        />
      </div>
    </>
  );
};

const mapStateToProps = state => {
  const { currentCurriculumProgramId } = getSelectedSchoolDetailsForCalendar(
    state
  );
  const currentCurriculumProgram = getSelectedCurriculumProgram(state);
  const organizationId = getOrganizationId(state);
  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );
  const canPublishToAll = canUserCreateEventForEveryone({
    currentCurriculumProgramId,
  });

  const programsUserCanPublishTo = _.filter(
    organizationCurriculumPrograms,
    ({ id }) =>
      ACLStorage.getPermissionValueByContext(
        CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
        [
          {
            entityId: id,
            entityType: "CURRICULUM_PROGRAM",
          },
        ]
      )
  );

  return {
    organizationId,
    queryFilter: canPublishToAll
      ? undefined
      : {
          curriculumProgramIds: _.map(programsUserCanPublishTo, ({ id }) => id),
        },
    curriculumPrograms: canPublishToAll
      ? organizationCurriculumPrograms
      : programsUserCanPublishTo,
    isData: true,
    isLoading: false,
    // style for the loading container
    containerStyle: {
      height: "100%",
    },
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    skip: ({ viewOnly }) => viewOnly,
    options: ({ organizationId, queryFilter }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        getCurriculumProgram: true,
        gradeFilters: queryFilter,
      },
    }),
    props({ getOrganizationGrades }) {
      const node = _.get(getOrganizationGrades, "node", {});

      return {
        isData: !_.isEmpty(node),
        grades: _.get(node, "grades"),
        isLoading: _.includes([1, 2], getOrganizationGrades.networkStatus),
      };
    },
  }),
  withLoader
)(GradeSelectionList);
